import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrangeVisitDialog from '../ui/ArrangeVisitDialog';
import LoginMiniForm from '../ui/LoginMiniForm';
import { useNavigate } from 'react-router-dom';
import SiteVisitDialog from './SiteVisitDialog';
import { log } from 'three/tsl';

function ValueAndPricing({
  dataField,
  propertyDetails,
  userDetails,
  recommendations,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [selectedRecommendationId, setSelectedRecommendationId] =
    useState(null);
  const [arrangedVisits, setArrangedVisits] = useState({});
  const [open, setOpen] = useState(false);
  const [isLoginFormDialogOpen, setIsLoginFormDialogOpen] = useState(false);

  // Extract values from dataField
  const data = dataField?.data || {};
  const output = dataField?.output || {};
  const openArrangeSiteVisit = (recommendationId) => {
    setSelectedRecommendationId(recommendationId);
    setIsDialogOpen(true);
  };
  const closeArrangeSiteVisit = (isVisitCreated, matchId) => {
    setIsDialogOpen(false);
    setSelectedConfigId(null);
    if (isVisitCreated) {
      setArrangedVisits({
        ...arrangedVisits,
        [selectedRecommendationId]: true,
      });
    }
  };

  const closeLoginForm = (didUserLogin = false) => {
    setIsLoginFormDialogOpen(false);
    if (didUserLogin) navigate('/dashboard');
  };
  const handleRequestCall = async () => {
    setIsLoginFormDialogOpen(true);
  };
  const benefits = {
    estimated_generation: dataField?.output?.solarProducedUnits || 0,
    expense_avoided:
      (dataField?.output?.solarProducedUnits || 0) *
        5 *
        (
          (dataField?.data?.billsWithoutSolar || 0) /
          (dataField?.data?.unitsWithoutSolar || 1)
        ).toFixed(1) || 0,
    subsidies: {
      central: parseInt(output?.centralGovtSubsidy || 0),
      state: parseInt(output?.stateGovtSubsidy || 0),
      onetime: 0,
      generation: 0,
    },
    total_benefits: 0, // Will be calculated below
    max_price: output?.max_cost || 0,
    final_price: output?.min_cost || 0,
    value_discount: output?.billReduction || 0,
  };
  const handleNext = () => {
    navigate(`/site-details?id=${propertyDetails?.id}&step=1`);
  };

  benefits.total_benefits =
    benefits.expense_avoided + parseInt(output?.subsidyAmount || 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'decimal',
      maximumFractionDigits: 0,
    }).format(amount);
  };
  console.log('dataField', dataField);

  if (!dataField) {
    return <></>;
  }

  return (
    <Stack spacing={6} sx={{ width: '100%', pt: 8, pb: 8 }}>
      <Typography
        variant='h4'
        component='h1'
        fontWeight='bold'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: {
            xs: theme.typography.h5.fontSize,
            md: theme.typography.h4.fontSize,
          },
        }}
      >
        <CurrencyRupeeIcon sx={{ fontSize: 'inherit' }} />
        Value & pricing
      </Typography>

      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Typography
            fontWeight='bold'
            variant={isMobile ? 'body1' : 'h6'}
            color='success.main'
          >
            Estimated generation
          </Typography>
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            fontWeight='bold'
            color={'success.main'}
          >
            {formatCurrency(benefits.estimated_generation)} units
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Typography fontWeight='bold' variant={isMobile ? 'body1' : 'h6'}>
            Expense avoided in 5 years {isMobile && <br />} (rate you pay{' '}
            {(
              (dataField?.data?.billsWithoutSolar || 0) /
              (dataField?.data?.unitsWithoutSolar || 1)
            ).toFixed(1)}{' '}
            per unit)
          </Typography>
          <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='bold'>
            ₹{formatCurrency(benefits.expense_avoided)}
          </Typography>
        </Box>

        {/* <Divider /> */}

        <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='bold'>
          Subsidies
        </Typography>

        <Stack spacing={1} pl={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>Central government subsidy</Typography>
            <Typography fontWeight='bold'>
              ₹{formatCurrency(benefits.subsidies.central)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>State government subsidy</Typography>
            <Typography fontWeight='bold'>
              ₹{formatCurrency(benefits.subsidies.state)}
            </Typography>
          </Box>
          {/* <Box sx={{ pl: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Typography>One time subsidy</Typography>
            <Typography fontWeight='bold'>
              ₹{formatCurrency(benefits.subsidies.onetime)}
            </Typography>
          </Box>

          <Box sx={{ pl: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Typography>Generation incentive over 5 years</Typography>
            <Typography fontWeight='bold'>
              ₹{formatCurrency(benefits.subsidies.generation)}
            </Typography>
          </Box> */}
        </Stack>

        <Divider />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='bold'>
            Total value of benefits {isMobile && <br />} over 5 years
            <br />
            <Typography
              variant='subtitle1'
              fontWeight='normal'
              mt={1}
              color='text'
            >
              Expense avoided + subsidies
            </Typography>
          </Typography>
          <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='bold'>
            ₹{formatCurrency(benefits.total_benefits)}
          </Typography>
        </Box>

        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='bold'>
              Payable price
            </Typography>
            <Stack alignItems='flex-end'>
              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                sx={{
                  textDecoration: 'line-through',
                  color: 'text.secondary',
                }}
              >
                ₹{formatCurrency(benefits.total_benefits)}
              </Typography>
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                fontWeight='bold'
                color='primary'
              >
                ₹{formatCurrency(benefits.max_price)}
              </Typography>
            </Stack>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='body1'>Value discount</Typography>
          <Stack alignItems='flex-end'>
            <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='bold'>
              {Math.round(
                (1 - benefits.max_price / benefits.total_benefits) * 100,
              )}
              %
            </Typography>
          </Stack>
        </Box>

        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          justifyContent={isMobile ? 'center' : 'flex-end'}
          pt={8}
        >
          {/* <Button
            variant='outlined'
            disableElevation
            startIcon={<CurrencyRupeeIcon />}
            sx={{ textTransform: 'none' }}
          >
            I am ready to pay
          </Button> */}
          {/* <Button
            variant='contained'
            disableElevation
            onClick={() => {
              if (isLoggedIn()) {
                handleQuote(66, propertyDetails?.userId);
              } else {
                handleRequestCall();
              }
            }}
            startIcon={quoteStatus ? <Check /> : <FeedIcon />}
            // disabled={quoteStatus || vendor?.isConverted}
            size={isMobile ? 'small' : 'default'}
          >
            {quoteStatus ? 'Request sent' : 'Request quote'}
          </Button> */}
          <Button
            variant='contained'
            color='primary'
            disableElevation
            startIcon={<ScheduleIcon />}
            sx={{ textTransform: 'none' }}
            onClick={() => {
              openArrangeSiteVisit(66);
            }}
          >
            Schedule a visit
            {/* {offer?.isConverted || arrangedVisits[offer?.id]
                          ? 'Visit requested'
                          : 'Request visit'} */}
          </Button>
        </Stack>
        <ArrangeVisitDialog
          open={isDialogOpen}
          handleClose={closeArrangeSiteVisit}
          propertyDetails={propertyDetails}
          vendorId={66}
          userDetails={userDetails}
          configId={selectedConfigId}
          recommendationId={selectedRecommendationId}
        />
      </Stack>
      {/* <SiteVisitDialog open={open} setOpen={setOpen} handleNext={handleNext} /> */}
      {/* <LoginMiniForm
        open={isLoginFormDialogOpen}
        handleClose={closeLoginForm}
        vendorId={66}
      /> */}
    </Stack>
  );
}

export default ValueAndPricing;
